import React, { useMemo, useState } from 'react';
import {
  GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Sort, RowSelectEventArgs,
} from '@syncfusion/ej2-react-grids';
import { Box, Typography, TextField, Stack, IconButton } from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { ItemMasterEntryDetailDialog } from '../dialogs/ItemMasterEntryDetailDialog';
import type { ItemMasterEntryView } from '../../../claims';

interface ItemMasterGridRow extends ItemMasterEntryView {
  status: string;
}

interface ItemMasterGridProps {
  entries: ItemMasterEntryView[];
}

interface ItemMasterGridEmptyStateProps {
  searchTerm: string;
}

/** Isolated so its branching doesn't count against `ItemMasterGrid`'s own complexity. */
function ItemMasterGridEmptyState({ searchTerm }: ItemMasterGridEmptyStateProps) {
  const isSearchResult = searchTerm.trim().length > 0;

  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
        {isSearchResult ? 'No items match your search' : 'No item master entries found'}
      </Typography>
      <Typography variant="body2" sx={{ color: '#94a3b8' }}>
        {isSearchResult
          ? `Try adjusting your search term: "${searchTerm}"`
          : 'No item master entries available for this facility'}
      </Typography>
    </Box>
  );
}

function matchesSearch(entry: ItemMasterEntryView, searchLower: string): boolean {
  return (
    entry.itemNum.toLowerCase().includes(searchLower) ||
    entry.itemDesc.toLowerCase().includes(searchLower) ||
    entry.cpt.toLowerCase().includes(searchLower)
  );
}

function findRowEntry(entries: ItemMasterEntryView[], itemNum: string | undefined) {
  return itemNum ? entries.find((entry) => entry.itemNum === itemNum) : undefined;
}

function toGridRow(entry: ItemMasterEntryView): ItemMasterGridRow {
  return { ...entry, status: entry.active ? 'Active' : 'Inactive' };
}

interface ItemMasterGridSearchBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

/** Isolated so its branching doesn't count against `ItemMasterGrid`'s own complexity. */
function ItemMasterGridSearchBar({ searchTerm, onSearchChange }: ItemMasterGridSearchBarProps) {
  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <SearchIcon sx={{ color: '#64748b' }} />
        <TextField
          fullWidth
          size="small"
          placeholder="Search by item #, description, or CPT..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#ffffff',
              '& fieldset': {
                borderColor: '#e2e8f0',
              },
              '&:hover fieldset': {
                borderColor: '#3b82f6',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3b82f6',
              }
            }
          }}
        />
        {searchTerm && (
          <IconButton size="small" onClick={() => onSearchChange('')} sx={{ color: '#64748b' }}>
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Box>
  );
}

interface ItemMasterGridTableProps {
  rows: ItemMasterGridRow[];
  isEmpty: boolean;
  searchTerm: string;
  onRowSelected: (args: RowSelectEventArgs) => void;
}

/** Isolated so its branching doesn't count against `ItemMasterGrid`'s own complexity. */
function ItemMasterGridTable({ rows, isEmpty, searchTerm, onRowSelected }: ItemMasterGridTableProps) {
  if (isEmpty)
    return <ItemMasterGridEmptyState searchTerm={searchTerm} />;

  return (
    <GridComponent
      dataSource={rows}
      height={424}
      allowPaging={true}
      allowSorting={true}
      pageSettings={{ pageSize: 25 }}
      selectionSettings={{ mode: 'Row', type: 'Single' }}
      rowSelected={onRowSelected}
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        backgroundColor: '#ffffff'
      }}
      enableHover={true}
    >
      <ColumnsDirective>
        <ColumnDirective field="itemNum" headerText="Item #" width="120" textAlign="Left" />
        <ColumnDirective field="itemDesc" headerText="Description" width="220" textAlign="Left" />
        <ColumnDirective field="cpt" headerText="CPT" width="100" textAlign="Left" />
        <ColumnDirective field="opChargeAmt" headerText="OP Charge Amt" width="130" textAlign="Right" type="number" format="C2" />
        <ColumnDirective field="ipChargeAmt" headerText="IP Charge Amt" width="130" textAlign="Right" type="number" format="C2" />
        <ColumnDirective field="unitCost" headerText="Unit Cost" width="120" textAlign="Right" type="number" format="C2" />
        <ColumnDirective field="manufacturerCatNum" headerText="Mfg Cat Num" width="140" textAlign="Left" />
        <ColumnDirective field="revCode" headerText="Rev Code" width="110" textAlign="Left" />
        <ColumnDirective field="status" headerText="Status" width="100" textAlign="Left" />
      </ColumnsDirective>
      <Inject services={[Page, Sort]} />
    </GridComponent>
  );
}

/**
 * Item-master entries grid (CAM-C10 US3 feasibility experiment). The full facility result set is
 * loaded once (see `useGetItemMasterEntriesQuery`) and paged/sorted/filtered entirely client-side
 * with Syncfusion's native `Page`/`Sort` modules — the same pattern as `RoomGrid`/`PayerGrid` —
 * rather than the previous continuation-token server paging.
 */
export function ItemMasterGrid({ entries }: ItemMasterGridProps) {
  const [selectedEntry, setSelectedEntry] = useState<ItemMasterEntryView | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredEntries = useMemo(() => {
    const searchLower = searchTerm.trim().toLowerCase();
    return searchLower ? entries.filter((entry) => matchesSearch(entry, searchLower)) : entries;
  }, [entries, searchTerm]);

  const rows = useMemo<ItemMasterGridRow[]>(() => filteredEntries.map(toGridRow), [filteredEntries]);

  const handleRowSelection = (args: RowSelectEventArgs) => {
    const row = args.data as ItemMasterGridRow | undefined;
    setSelectedEntry(findRowEntry(entries, row?.itemNum) ?? null);
  };

  return (
    <Box>
      <ItemMasterGridSearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />

      <ItemMasterGridTable
        rows={rows}
        isEmpty={filteredEntries.length === 0}
        searchTerm={searchTerm}
        onRowSelected={handleRowSelection}
      />

      {selectedEntry && (
        <ItemMasterEntryDetailDialog open onClose={() => setSelectedEntry(null)} entry={selectedEntry} />
      )}
    </Box>
  );
}
